import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    FormHelperText
} from "@material-ui/core";
import { connect } from "react-redux";
import * as Yup from "yup";
import * as actions from '../../sc-settings/LabelTemplate/store/labelTemplateActions';

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    cancel: {
        width: "120px",
        marginTop: "10px",
        marginRight: "10px",
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default

        }
    },
    formControl: {
        width: "100%"
    },
    buttons: {
        textAlign: "right"
    }
});

const PrinterForm = props => {
    const { classes, formSettings, create, types, initialValues, loading, handleSubmit, translations, onFetchLabelTemplates, activeCustomer } = props;
    const [workLabelTemplates, setWorkLabelTemplates] = useState([]);
    const [mobileLabelTemplate, setMobileLabelTemplate] = useState([]);
    const [printerType, setPrinterType] = useState(initialValues?.PrinterType ?? types[0].value);

    useEffect(()=>{
        const fetchLabels = async() => {
            const workResult = await onFetchLabelTemplates({ ActiveCustomerID: activeCustomer, q: { ProductType: ['PCProduct'], Type: ['system', 'custom'], Format: printerType } })
            if (workResult){
                setWorkLabelTemplates(workResult?.data);
            }
            const mobileResult = await onFetchLabelTemplates({ ActiveCustomerID: activeCustomer, q: { ProductType: ['MobileProduct'], Type: ['system', 'custom'], Format: printerType } })
            if (mobileResult){
                setMobileLabelTemplate(mobileResult?.data)
            }
        }
        fetchLabels()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[activeCustomer, printerType])

    return (
        <div className={create ? classes.root : null}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);

                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                name="Name"
                                value={values.Name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations["name"] || "Name"}
                                label={translations["name"] || "Name"}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['printer_type_text'] || "Please select printer type value"}</InputLabel>
                                <Select
                                    name="PrinterType"
                                    label={translations['printer_type'] || 'Printer type'}
                                    margin={"dense"}
                                    value={printerType}
                                    onChange={(event) => {
                                        setPrinterType(event.target.value);
                                        handleChange(event);
                                    }}
                                >
                                    {(types ? [...types] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value || ""}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['connection_type_text'] || "Please select the connection type"}</InputLabel>
                                <Select
                                    name="ConnectionType"
                                    label={translations["connection_type"] || 'Connection Type'}
                                    margin={"dense"}
                                    value={values.ConnectionType}
                                    onChange={handleChange}
                                >
                                    <MenuItem key='network' value='network'>
                                        {translations['network'] || 'Network'}
                                    </MenuItem>
                                    <MenuItem key='usb' value='usb'>
                                        {translations['usb'] || 'USB'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {values.ConnectionType === 'network' ? 
                                <>
                                    <TextField
                                        name="URI"
                                        value={values.URI}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.URI && touched.URI)}
                                        placeholder={translations["uri"] || "URI"}
                                        label={translations["uri"] || "URI"}
                                        margin={"dense"}
                                        fullWidth={true}
                                    />
                                    <FormHelperText>{translations["example"] || "Example"}: {values.PrinterType==="zebra"? "192.168.1.1:9100" : values.PrinterType==="godex"? "192.168.1.1:2000" : values.PrinterType === "brother" ? "192.168.1.1:9100" : null}</FormHelperText>
                                </> : null}
                            <div style={{display: 'flex', justifyContent: 'center', columnGap: '1.5rem'}}>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["work_label_template_select"] || "Please select work label template"}</InputLabel>
                                    <Select
                                        name="WorkLabelTemplateID"
                                        label={translations["label_template"] || 'Label template'}
                                        margin={"dense"}
                                        value={values.WorkLabelTemplateID}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>{translations["none"] || "None"}</em>
                                        </MenuItem>
                                        {(workLabelTemplates ? workLabelTemplates : []).map(option => (
                                            <MenuItem key={option.LabelTemplateID} value={option.LabelTemplateID}>
                                                {`${translations[option.Purpose]} - ${option.Name} (${option.PaperWidth}x${option.PaperHeight})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["mobile_label_template_select"] || "Please select mobile label template"}</InputLabel>
                                    <Select
                                        name="MobileLabelTemplateID"
                                        label={translations["label_template"] || 'Label template'}
                                        margin={"dense"}
                                        value={values.MobileLabelTemplateID}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>{translations["none"] || "None"}</em>
                                        </MenuItem>
                                        {(mobileLabelTemplate ? mobileLabelTemplate : []).map(option => (
                                            <MenuItem key={option.LabelTemplateID} value={option.LabelTemplateID}>
                                                {`${translations[option.Purpose]} - ${option.Name} (${option.PaperWidth}x${option.PaperHeight})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div className={classes.buttons}>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        type="submit"
                                        disabled={loading || !(isValid && dirty) || isSubmitting}
                                    >
                                        {loading ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchLabelTemplates: data => dispatch(actions.fetchLabelTemplates(data))
    };
};

export default connect(
    null,
    mapDispatchToProps)(withStyles(styles, { withTheme: true })(PrinterForm));
